import * as fromActions from '../../actions';
import * as fromShared from '../../../shared';
import * as fromStates from '../../states';

import { Actions, Effect, ofType } from '@ngrx/effects';
/**************************************************************************************************/
/*   File: /src/app/stores/effects/ui/effects.ts                                                  */
/*   Project: newplan-frontend                                                                    */
/*   Created Date: Friday February 22nd 2019                                                      */
/*   Author: Andrea Zamboni andrea.zamboni@trilogis.it                                            */
/*   -----                                                                                        */
/*   Last Modified: Wed May 15 2019                                                               */
/*   Modified By: Andrea Zamboni                                                                  */
/*   -----                                                                                        */
/*   Copyright (c) 2019 Trilogis s.r.l. for SIAG                                                  */
/**************************************************************************************************/
import { EMPTY, of } from 'rxjs';
import { catchError, filter, map, mergeMap, switchMap, tap, withLatestFrom } from 'rxjs/operators';

import { ActivatedRoute } from '@angular/router';
import { Injectable } from '@angular/core';
import { RootState } from '../../states/root/state';
import { Store } from '@ngrx/store';
import { environment } from '../../../../environments/environment.test-env';

/**
 * The router effect
 */
@Injectable()
export class UiEffects {

    /**
     * The constructor
     * @param {ActivatedRoute} activatedRoute the angular activated route
     * @param {Actions} actions The actions
     * @param {fromShared.services.LanguageService} languageService The language service
     * @param {fromShared.services.DossierStatusService} dossierStatusService The dossier status service
     * @param {fromShared.services.PlanTypesService} planTypesService The plan type service
     * @param {fromShared.services.AmbitiService} ambitiService The ambiti service
     * @param {fromShared.services.GeoFileTypeService} geoFileTypeService The geo file type service
     */
    constructor(
        private activatedRoute: ActivatedRoute,
        private actions: Actions,
        private languageService: fromShared.services.LanguageService,
        private dossierStatusService: fromShared.services.DossierStatusService,
        private planTypesService: fromShared.services.PlanTypesService,
        private ambitiService: fromShared.services.AmbitiService,
        private geoFileTypeService: fromShared.services.GeoFileTypeService,
        private store: Store<RootState>,
        private modalService: fromShared.services.ModalService
    ) { }

    /**
     * The show confirm dialog
     */
    @Effect()
    showConfirmDialogOpen$ = this.actions.pipe(
        ofType<fromActions.fromMessages.ShowConfirmDialogAction>(
            fromActions.fromMessages.MessagesActionsType.SHOW_CONFIRM_DIALOG_OPEN
        ),
        switchMap((action: fromActions.fromMessages.ShowConfirmDialogAction) => {
            this.modalService.show(action.payload.elementRef);
            return EMPTY;
        })
    );

    /**
     * Effect for initialize language change request
     */
    @Effect()
    languageChangeRequest$ = this.actions.pipe(
        ofType<fromActions.fromUi.LanguageChangeAction>(fromActions.fromUi.UiActionTypes.LANGUAGE_CHANGE_ACTION),
        switchMap((action) =>
            // Check is is valid language
            this.languageService.isValidLanguage(action.payload.isoCode).pipe(
                map(valid =>
                    new fromActions.fromUi.LanguageChangeSuccessAction(
                        {
                            isoCode: action.payload.isoCode
                        }
                    )
                ),
                catchError(failure =>

                    of(
                        new fromActions.fromUi.LanguageChangeFailureAction(
                            {
                                message: new fromShared.models.Message(
                                    fromShared.enums.MessageType.WARNING,
                                    failure,
                                    fromShared.enums.MessagePositionEnum.TOP,
                                    fromStates.fromUi.STATE_NAME
                                )
                            }
                        )
                    )
                )
            )
        )
    );

    /**
     * Effect on language change success
     */
    @Effect()
    languageChangeSuccessEffect$ = this.actions.pipe(
        ofType<fromActions.fromUi.LanguageChangeSuccessAction>(fromActions.fromUi.UiActionTypes.LANGUAGE_CHANGE_SUCCESS_ACTION),
        switchMap((action) =>
            this.languageService.setLanguage(action.payload.isoCode).pipe(
                switchMap(valid =>
                    [
                        new fromActions.fromRouter.Go(
                            {
                                path: this.activatedRoute.snapshot.url,
                                query: {
                                    isoCode: action.payload.isoCode
                                }
                            }
                        ),
                    ]
                ),
                // tslint:disable-next-line
                withLatestFrom(this.store, (action, state) => state.ui.user),
                filter(user => user !== undefined),
                switchMap(user => [
                    new fromActions.fromUi.LoadAllAmbiti(),
                    new fromActions.fromUi.LoadPlanTypes(),
                    new fromActions.fromUi.LoadDossierStatus({
                        isoCode: action.payload.isoCode
                    }),
                    new fromActions.fromUi.LoadGeoDataTypes()
                ])
            )
        )
    );


    /**
     * Effect on language change failure
     */
    @Effect()
    languageChangeFailureEffect$ = this.actions.pipe(
        ofType<fromActions.fromUi.LanguageChangeFailureAction>(fromActions.fromUi.UiActionTypes.LANGUAGE_CHANGE_FAILURE_ACTION),
        switchMap(
            action => [
                new fromActions.fromUi.LanguageChangeAction(
                    {
                        isoCode: this.languageService.defaultLanguage
                    }
                )

            ]
        )
    );

    /**
     * Effect on set user
     */
    @Effect()
    setUserEffect$ = this.actions.pipe(
        ofType<fromActions.fromUi.SetLoggedUser>(fromActions.fromUi.UiActionTypes.SET_LOGGED_USER),
        mergeMap(
            action => [
                new fromActions.fromUi.LanguageChangeAction({
                    isoCode: action.payload.user && action.payload.user.language ? action.payload.user.language.toLowerCase() : environment.defaultLanguage
                })
            ]
        )
    );

    /**
     * Effect on set user
     */
    @Effect()
    unsetUserEffect$ = this.actions.pipe(
        ofType<fromActions.fromUi.UnSetLoggedUser>(fromActions.fromUi.UiActionTypes.UNSET_LOGGED_USER),
        switchMap(
            action => [
                new fromActions.fromUi.LanguageChangeAction({
                    isoCode: action.payload.isoCode
                })
            ]
        )
    );

    /**
     * Load the dossier status array
     */
    @Effect()
    loadDossierStatusEffect$ = this.actions.pipe(
        ofType<fromActions.fromUi.LoadDossierStatus>(fromActions.fromUi.UiActionTypes.LOAD_DOSSIER_STATUS),
        switchMap((action) =>
            this.dossierStatusService.getDossierStatus(action.payload.isoCode).pipe(
                map(status =>
                    new fromActions.fromUi.LoadDossierStatusSuccess(
                        {
                            status: status
                        }
                    )
                )
            )
        )
    );

    /**
     * Load the dossier status array
     */
    @Effect()
    loadPlanTypesEffect$ = this.actions.pipe(
        ofType<fromActions.fromUi.LoadPlanTypes>(fromActions.fromUi.UiActionTypes.LOAD_PLAN_TYPES),
        switchMap((action) =>
            this.planTypesService.getPlanTypes().pipe(
                map(planTypes =>
                    new fromActions.fromUi.LoadPlanTypesSuccess(
                        {
                            planTypes: planTypes
                        }
                    )
                ),
                catchError((fail) =>
                    of(
                        new fromActions.fromUi.LoadPlanTypesFail(
                            {
                                message: new fromShared.models.Message(
                                    fromShared.enums.MessageType.WARNING,
                                    fromShared.enums.Messages.PLAN_TYPES_FAILED,
                                    fromShared.enums.MessagePositionEnum.TOP,
                                    fromStates.fromUi.STATE_NAME
                                )
                            }
                        )
                    )
                )
            )
        )
    );

    /**
    * Load all ambiti
    */
    @Effect()
    loadAllAmbitiEffect$ = this.actions.pipe(
        ofType<fromActions.fromUi.LoadAllAmbiti>(
            fromActions.fromUi.UiActionTypes.LOAD_ALL_AMBITI
        ),
        switchMap((action) =>
            this.ambitiService.getAllAmbiti().pipe(
                map(ambiti =>
                    new fromActions.fromUi.LoadAllAmbitiSuccess(
                        {
                            ambiti: ambiti
                        }
                    )
                ),
                catchError((fail) =>
                    of(
                        new fromActions.fromUi.LoadAllAmbitiFail(
                            {
                                message: new fromShared.models.Message(
                                    fromShared.enums.MessageType.WARNING,
                                    fromShared.enums.Messages.ALL_AMBITI_FAIL,
                                    fromShared.enums.MessagePositionEnum.TOP,
                                    fromStates.fromUi.STATE_NAME
                                )
                            }
                        )
                    )
                )
            )
        )
    );

    /**
     * Load the geo data types
     */
    @Effect()
    loadGeoDataTypes$ = this.actions.pipe(
        ofType<fromActions.fromUi.LoadGeoDataTypes>(fromActions.fromUi.UiActionTypes.LOAD_GEO_DATA_TYPES),
        switchMap((action) =>
            this.geoFileTypeService.getGeoFileTypes().pipe(
                map((geoFileTypes) => {
                    return new fromActions.fromUi.LoadGeoDataTypesSuccess({
                        fileTypes: geoFileTypes
                    });
                })
            )
        )
    );

}
